import * as React from 'react';

import * as styles from './ScrollContainer.css';

export interface IScrollContainerHandle {
  scrollToBottom(buttonClickScroll?: boolean): void;
  readonly isScrolledToBottom: boolean;
}

interface IScrollContainerProps {
  showScrollBottomButton?: boolean;
  onButtonVisible?(isVisible: boolean): void;
  onScroll(scrollTop: number): void;
  // Отступ сверху в пикселях, через который можно запросить следующую страницу
  whenRequestNextPage?: number;
  // Можно ли запросить следующую страницу
  canRequestNextPage?: boolean;
  children: React.ReactNode;
}

export const ScrollContainer = React.forwardRef<IScrollContainerHandle, IScrollContainerProps>(
  ({ showScrollBottomButton, onButtonVisible, onScroll, children }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const innerRef = React.useRef<HTMLDivElement>(null);
    const previousScrollHeightRef = React.useRef<number>(0);
    const previousScrollTopRef = React.useRef<number>(0);
    const buttonClickScrollRef = React.useRef<boolean>(false);

    React.useImperativeHandle(
      ref,
      () => ({
        scrollToBottom(buttonClickScroll?: boolean) {
          const container = containerRef.current;
          if (!container) {
            return;
          }

          if (buttonClickScroll) {
            buttonClickScrollRef.current = true;
          }

          const { scrollHeight } = container;
          container.scrollTop = scrollHeight;
          previousScrollHeightRef.current = scrollHeight;
          previousScrollTopRef.current = container.scrollTop;

          if (showScrollBottomButton && onButtonVisible) {
            onButtonVisible(false);
          }
        },

        get isScrolledToBottom() {
          const container = containerRef.current;

          return container ? container.offsetHeight + container.scrollTop >= container.scrollHeight : false;
        },
      }),
      [showScrollBottomButton, onButtonVisible],
    );

    // Начальный скролл вниз до первого paint
    React.useLayoutEffect(() => {
      const container = containerRef.current;
      if (container) {
        previousScrollHeightRef.current = container.scrollHeight;
        container.scrollTop = container.scrollHeight;
        previousScrollTopRef.current = container.scrollTop;
      }
    }, []);

    // Сохранение позиции скролла при изменении высоты контента (загрузка старых сообщений)
    React.useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const inner = innerRef.current;
      if (!inner) {
        return;
      }

      const observer = new ResizeObserver(() => {
        if (buttonClickScrollRef.current) {
          return;
        }

        const { scrollHeight } = container;
        const scrollTop = scrollHeight - previousScrollHeightRef.current + previousScrollTopRef.current;
        previousScrollHeightRef.current = scrollHeight;
        container.scrollTop = scrollTop;
        previousScrollTopRef.current = container.scrollTop;
      });

      // Наблюдаем за внутренним элементом — он меняет размер когда грузятся
      // изображения и другой lazy-контент, даже если clientHeight контейнера не меняется
      observer.observe(inner);

      return () => {
        observer.disconnect();
      };
    }, []);

    const handleScroll = React.useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        const container = containerRef.current;
        if (!container) {
          return;
        }

        const { scrollTop } = e.currentTarget;
        const { scrollHeight } = container;
        const { height } = container.getBoundingClientRect();

        // Не обновляем prevScrollTop когда контент сжимается: браузер авто-зажимает
        // scrollTop до нового maxScrollTop и стреляет scroll-событием, но это не
        // пользовательский скролл. ResizeObserver пересчитает позицию корректно сам.
        if (scrollHeight >= previousScrollHeightRef.current) {
          previousScrollTopRef.current = scrollTop;
        }

        if (showScrollBottomButton && onButtonVisible) {
          onButtonVisible(scrollTop + 100 <= scrollHeight - height);
        }

        onScroll(scrollTop);
      },
      [showScrollBottomButton, onButtonVisible, onScroll],
    );

    return (
      <div ref={containerRef} onScroll={handleScroll} className={styles['container']}>
        <div ref={innerRef} className={styles['inner']}>
          {children}
        </div>
      </div>
    );
  },
);

ScrollContainer.displayName = 'ScrollContainer';
